import { Button, Divider, Group, Stack, Text, Title } from "@mantine/core";
import { modals } from "@mantine/modals";
import { NotificationData, notifications } from "@mantine/notifications";
import { IconCheck, IconInfoCircle, IconX } from "@tabler/icons-react";
import { ReactNode } from "@tanstack/react-router";
import { useState } from "react";
import { createAtom } from "shared/utils/atom";
import { logger } from "shared/utils/logger";
import { maybe } from "shared/utils/maybe";

type AsyncModalCtrl = Disposable & {
    close: () => void;
};

export const notify = {
    ...notifications,
    catch: (error: unknown) => {
        logger.error(error);
        return notifications.show({
            color: "red",
            title: "Error",
            icon: <IconX size={20} />,
            message: error instanceof Error ? error.message : (error as object).toString(),
            closeButtonProps: { "aria-label": "Close notification" },
        });
    },
    confirm: async (body: ReactNode, opts?: { label?: string; cancelLabel?: string; title?: string; color?: string }) => {
        const { resolve, reject, promise } = Promise.withResolvers<AsyncModalCtrl>();
        const id = createAtom<string>();

        function Panel() {
            const [isLoading, setIsLoading] = useState(false);

            return (
                <Stack>
                    {typeof body == "string" ? <Text size="sm">{body}</Text> : body}
                    <Divider mx="-md" />
                    <Group justify="flex-end">
                        <Button
                            variant="default"
                            onClick={() => {
                                reject();
                                maybe(id.getState().value)?.take(modals.close);
                            }}
                        >
                            {opts?.cancelLabel ?? "Cancel"}
                        </Button>
                        <Button
                            loading={isLoading}
                            color={opts?.color == "primary" ? "var(--mantine-color-primary)" : opts?.color}
                            onClick={() => {
                                setIsLoading(true);
                                resolve({
                                    close() {
                                        setIsLoading(false);
                                        maybe(id.getState().value)?.take(modals.close);
                                    },
                                    [Symbol.dispose]() {
                                        maybe(id.getState().value)?.take(modals.close);
                                    },
                                });
                            }}
                        >
                            {opts?.label || "Confirm"}
                        </Button>
                    </Group>
                </Stack>
            );
        }

        id.setState({
            value: modals.open({
                title: <Title order={3}>{opts?.title ?? "Confirm Action"}</Title>,
                children: <Panel />,
            }),
        });

        return promise;
    },
    show: {
        error: (props: NotificationData) => {
            return notifications.show({
                color: "red",
                title: "Error",
                icon: <IconX size={20} />,
                closeButtonProps: { "aria-label": "Close notification" },
                ...props,
            });
        },
        warn: (props: NotificationData) => {
            if (!!window.interop?.onToast) {
                return window.interop.onToast("info", typeof props == "string" ? props : (props.message?.toString() ?? "Unknown"));
            }

            return notifications.show({
                color: "yellow",
                title: "Warning",
                icon: <IconInfoCircle size={20} />,
                closeButtonProps: { "aria-label": "Close notification" },
                ...props,
            });
        },
        info: (props: NotificationData) => {
            if (!!window.interop?.onToast) {
                return window.interop.onToast("info", typeof props == "string" ? props : (props.message?.toString() ?? "Unknown"));
            }

            return notifications.show({
                color: "blue",
                title: "Info",
                icon: <IconInfoCircle size={20} />,
                closeButtonProps: { "aria-label": "Close notification" },
                ...props,
            });
        },
        success: (props: NotificationData | string) => {
            if (!!window.interop?.onToast) {
                return window.interop.onToast("success", typeof props == "string" ? props : (props.message?.toString() ?? "Unknown"));
            }

            if (typeof props == "string") {
                return notifications.show({
                    color: "green",
                    title: "Success",
                    icon: <IconCheck size={20} />,
                    message: props,
                    closeButtonProps: { "aria-label": "Close notification" },
                });
            }

            return notifications.show({
                color: "green",
                title: "Success",
                icon: <IconCheck size={20} />,
                closeButtonProps: { "aria-label": "Close notification" },
                ...props,
            });
        },
    },
} as const;
